import { useState, useEffect } from "react";
import styled from "styled-components";
import { XMarkIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import Table from "../../common/components/Table";
import { ChatSession } from "../container/ChatHistoryContainer.hook";

interface UserHistoryModalProps {
  isOpen: boolean;
  sessions: ChatSession[];
  userPhone: string;
  formatDate: (date: string) => string;
  onClose: () => void;
  onSessionDetailClick: (session: ChatSession) => void;
}

function UserHistoryModal({
  isOpen,
  sessions,
  userPhone,
  formatDate,
  onClose,
  onSessionDetailClick,
}: UserHistoryModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // 모달이 열릴 때마다 첫 페이지로 리셋
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // 페이지네이션 계산
  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentSessions = sessions.slice(startIndex, endIndex);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <Title>사용자 채팅 내역</Title>
            <UserInfo>
              <InfoLabel>사용자:</InfoLabel>
              <InfoValue>{userPhone}</InfoValue>
              <SessionCount>{sessions.length}개의 세션</SessionCount>
            </UserInfo>
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Table.Wrapper>
            <Table.Header
              tabs={[
                { label: "세션 제목", className: "w-[30%]" },
                { label: "세션 생성일", className: "w-[30%]" },
                { label: "대화수", className: "w-[20%]" },
                { label: "세션 내용", className: "w-[20%]" },
              ]}
            />
            <Table.Body isLoading={false} isEmpty={sessions.length === 0}>
              {currentSessions.map((session) => (
                <Table.Row key={session.id}>
                  <Table.Column className="w-[30%]">
                    <span
                      className="truncate"
                      title={session.title}
                    >
                      {session.title}
                    </span>
                  </Table.Column>
                  <Table.Column className="w-[30%]">
                    <span
                      className="truncate"
                      title={formatDate(session.createdAt)}
                    >
                      {formatDate(session.createdAt)}
                    </span>
                  </Table.Column>
                  <Table.Column className="w-[20%]">
                    <span>{session.messageCount}</span>
                  </Table.Column>
                  <Table.Column className="w-[20%]">
                    <button
                      onClick={() => onSessionDetailClick(session)}
                      className="flex items-center gap-1 rounded bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
                      title="세션 내용 보기"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      <span>보기</span>
                    </button>
                  </Table.Column>
                </Table.Row>
              ))}
            </Table.Body>
            {sessions.length > ITEMS_PER_PAGE && (
              <div className="border border-t-0 border-zinc-300 bg-white py-4">
                <Table.Pagination
                  page={currentPage}
                  total={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </Table.Wrapper>
        </ModalBody>

        <ModalFooter>
          <CloseButtonLarge onClick={onClose}>닫기</CloseButtonLarge>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
}

const Overlay = styled.div(() => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}));

const ModalContainer = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  borderRadius: 12,
  width: "90%",
  maxWidth: 1000,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
}));

const ModalHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: theme.spacing.large,
  borderBottom: `2px solid ${theme.color.lightGray}`,
}));

const HeaderContent = styled.div(() => ({
  flex: 1,
}));

const Title = styled.h2(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing.small,
  fontSize: 24,
  fontWeight: "bold",
  color: theme.color.black,
}));

const UserInfo = styled.div(() => ({
  display: "flex",
  gap: 12,
  alignItems: "center",
  marginTop: 12,
}));

const InfoLabel = styled.span(({ theme }) => ({
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.gray,
}));

const InfoValue = styled.span(({ theme }) => ({
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.black,
}));

const SessionCount = styled.span(({ theme }) => ({
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.lightNavy,
  marginLeft: 8,
}));

const CloseButton = styled.button(({ theme }) => ({
  padding: 8,
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: theme.color.gray,
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: theme.color.lightGray,
    color: theme.color.black,
  },
}));

const ModalBody = styled.div(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing.large,
}));

const ModalFooter = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing.large,
  borderTop: `2px solid ${theme.color.lightGray}`,
}));

const CloseButtonLarge = styled.button(({ theme }) => ({
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: "600",
  color: theme.color.white,
  backgroundColor: theme.color.gray,
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: theme.color.black,
  },
}));

export default UserHistoryModal;
